import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { ColumnGroup } from "primereact/columngroup";
import { Row } from "primereact/row";
import { Button } from "primereact/button";
import { Card } from "primereact/card";
import { Toast } from "primereact/toast";
import { MultiSelect } from "primereact/multiselect";
import Menu from "../Menu";
import Loading from "../Loading";

import api from "../../services/api";

const compactHeaderStyle = {
  fontSize: 12,
  padding: "5px 7px",
  whiteSpace: "nowrap",
};

const compactBodyStyle = {
  fontSize: 12,
  padding: "4px 7px",
  whiteSpace: "nowrap",
};

const numericBodyStyle = {
  ...compactBodyStyle,
  textAlign: "right",
};

const monthNames = [
  "Janeiro",
  "Fevereiro",
  "Março",
  "Abril",
  "Maio",
  "Junho",
  "Julho",
  "Agosto",
  "Setembro",
  "Outubro",
  "Novembro",
  "Dezembro",
];

const articleSorter = new Intl.Collator("pt-BR", {
  numeric: true,
  sensitivity: "base",
});

const defaultMaxIndexPercentage = 200;
const maxIndexPercentageStorageKey = "indiceVendaCorte.maxIndexPercentage";

const metricStyles = {
  sales: {
    headerStyle: {
      ...compactHeaderStyle,
      backgroundColor: "#cfe8ff",
      textAlign: "right",
    },
    bodyStyle: { ...numericBodyStyle, backgroundColor: "#e8f4ff" },
  },
  cuts: {
    headerStyle: {
      ...compactHeaderStyle,
      backgroundColor: "#ffe0b2",
      textAlign: "right",
    },
    bodyStyle: { ...numericBodyStyle, backgroundColor: "#fff0d6" },
  },
  index: {
    headerStyle: {
      ...compactHeaderStyle,
      backgroundColor: "#c8e6c9",
      textAlign: "right",
    },
    bodyStyle: {
      ...numericBodyStyle,
      backgroundColor: "#e3f3e4",
      fontWeight: "bold",
    },
  },
};

const fixedColumns = [
  {
    field: "article",
    header: "Artigo",
    sourceFields: ["article", "artigo"],
    bodyStyle: { ...compactBodyStyle, fontWeight: "bold" },
    sortable: true,
  },
  {
    field: "tear_qtde",
    header: "Nº Teares",
    bodyStyle: { ...compactBodyStyle, fontWeight: "bold" },
    sortable: true,
  },
  {
    field: "familia",
    header: "Família",
    bodyStyle: { ...compactBodyStyle, fontWeight: "bold" },
    sortable: true,
  },
  {
    field: "class1",
    header: "1º",
    bodyStyle: numericBodyStyle,
    format: formatInteger,
  },
  {
    field: "class2",
    header: "2º",
    bodyStyle: numericBodyStyle,
    format: formatInteger,
  },
  {
    field: "class3",
    header: "3º",
    bodyStyle: numericBodyStyle,
    format: formatInteger,
  },
  {
    field: "class4",
    header: "4º",
    bodyStyle: numericBodyStyle,
    format: formatInteger,
  },
  {
    field: "acabamento",
    header: "Acabamento",
    bodyStyle: numericBodyStyle,
    format: formatInteger,
  },
  {
    field: "acabado",
    header: "Acabado",
    bodyStyle: numericBodyStyle,
    format: formatInteger,
  },
  {
    field: "revisao",
    header: "Revisão",
    bodyStyle: numericBodyStyle,
    format: formatInteger,
  },
  {
    field: "revisado",
    header: "Revisado",
    bodyStyle: numericBodyStyle,
    format: formatInteger,
  },
  {
    field: "tecelagem",
    header: "Tecelagem",
    bodyStyle: numericBodyStyle,
    format: formatInteger,
  },
  {
    field: "tear_estoque",
    header: "Estoque Tear",
    bodyStyle: numericBodyStyle,
    format: formatInteger,
  },
  {
    field: "total_estoque",
    header: "Estoque Total",
    bodyStyle: numericBodyStyle,
    format: formatInteger,
  },
];

const metricColumns = [
  {
    key: "sales",
    suffix: "Sales",
    header: "Venda",
    sourceFields: ["sales", "venda"],
    format: formatInteger,
  },
  {
    key: "cuts",
    suffix: "Cuts",
    header: "Corte",
    sourceFields: ["cuts", "corte"],
    format: formatInteger,
  },
  {
    key: "index",
    suffix: "Index",
    header: "V/C",
    sourceFields: ["index", "indice"],
    format: formatIndex,
  },
];

function createPeriodKey(period) {
  return String(period)
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-zA-Z0-9]/g, "");
}

function formatPeriod(period) {
  if (!period || !/^\d{2}-\d{2}$/.test(period)) {
    return period;
  }

  const [year, month] = period.split("-");
  const monthName = monthNames[Number(month) - 1];

  if (!monthName) {
    return period;
  }

  return `${monthName}/20${year}`;
}

function getPeriodDate(period) {
  const [monthName, year] = period.split("/");

  return new Date(Number(year), monthNames.indexOf(monthName), 1);
}

function calculateIndex(sales, cuts) {
  const salesNumber = Number(sales);
  const cutsNumber = Number(cuts);

  if (!cutsNumber) {
    return null;
  }

  return ((salesNumber || 0) / cutsNumber) * 100;
}

function formatInteger(value) {
  if (value === null || value === undefined || value === "") {
    return "";
  }

  const numberValue = Number(value);

  if (Number.isNaN(numberValue)) {
    return value;
  }

  if (numberValue === 0) {
    return "";
  }

  return Math.round(numberValue).toLocaleString("pt-BR", {
    maximumFractionDigits: 0,
  });
}

function getStoredMaxIndexPercentage() {
  if (typeof localStorage === "undefined") {
    return defaultMaxIndexPercentage;
  }

  const storedValue = localStorage.getItem(maxIndexPercentageStorageKey);
  const numberValue = Number(storedValue);

  return Number.isFinite(numberValue) && numberValue > 0
    ? numberValue
    : defaultMaxIndexPercentage;
}

function saveMaxIndexPercentage(value) {
  if (typeof localStorage === "undefined") {
    return;
  }

  localStorage.setItem(maxIndexPercentageStorageKey, String(value));
}

function formatIndex(value, maxIndexPercentage) {
  if (value === null || value === undefined || value === "") {
    return "";
  }

  const numberValue = Number(value);

  if (Number.isNaN(numberValue)) {
    return value;
  }

  if (numberValue === 0) {
    return "";
  }

  if (numberValue > maxIndexPercentage) {
    return `+${maxIndexPercentage}%`;
  }

  return `${Math.round(numberValue).toLocaleString("pt-BR", {
    maximumFractionDigits: 0,
  })}%`;
}

function getFirstAvailableValue(item, fields) {
  const field = fields.find(
    (currentField) =>
      item[currentField] !== null && item[currentField] !== undefined,
  );

  return field ? item[field] : undefined;
}

function buildTableData(items) {
  const monthMap = new Map();
  const rowMap = new Map();

  items.forEach((item) => {
    const period = formatPeriod(
      getFirstAvailableValue(item, ["periodo", "período", "period"]),
    );

    if (!period) {
      return;
    }

    const periodKey = createPeriodKey(period);
    const article = getFirstAvailableValue(item, ["article", "artigo"]);
    const row =
      rowMap.get(article) ||
      fixedColumns.reduce((rowData, column) => {
        rowData[column.field] = getFirstAvailableValue(item, [
          ...(column.sourceFields || []),
          column.field,
        ]);

        return rowData;
      }, {});

    monthMap.set(period, true);
    metricColumns.forEach((column) => {
      row[`${periodKey}${column.suffix}`] = getFirstAvailableValue(
        item,
        column.sourceFields,
      );
    });
    row[`${periodKey}Index`] =
      row[`${periodKey}Index`] ??
      calculateIndex(row[`${periodKey}Sales`], row[`${periodKey}Cuts`]);
    rowMap.set(article, row);
  });

  const months = Array.from(monthMap.keys()).sort(
    (firstPeriod, secondPeriod) =>
      getPeriodDate(firstPeriod) - getPeriodDate(secondPeriod),
  );

  return {
    rows: Array.from(rowMap.values()),
    months,
  };
}

function renderMetricLegend() {
  return (
    <div className="p-d-flex p-ai-center p-mb-2" style={{ fontSize: 12 }}>
      {metricColumns.map((column, index) => (
        <span
          key={column.key}
          className={index < metricColumns.length - 1 ? "p-mr-3" : undefined}
        >
          <span
            style={{
              ...metricStyles[column.key].headerStyle,
              display: "inline-block",
              width: 11,
              height: 11,
              marginRight: 5,
            }}
          />
          {column.header}
        </span>
      ))}
    </div>
  );
}

export default function TabelaIndiceVendaCorte() {
  const toast = useRef(null);
  const initialMaxIndexPercentage = useMemo(getStoredMaxIndexPercentage, []);
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [monthColumns, setMonthColumns] = useState([]);
  const [selectedArticles, setSelectedArticles] = useState([]);
  const [maxIndexPercentage, setMaxIndexPercentage] = useState(
    initialMaxIndexPercentage,
  );
  const [maxIndexPercentageInput, setMaxIndexPercentageInput] = useState(() =>
    String(initialMaxIndexPercentage),
  );
  const articleOptions = useMemo(
    () =>
      rows
        .map((row) => row.article)
        .filter((article) => article !== null && article !== undefined)
        .filter(
          (article, index, articles) => articles.indexOf(article) === index,
        )
        .sort((firstArticle, secondArticle) =>
          articleSorter.compare(String(firstArticle), String(secondArticle)),
        )
        .map((article) => ({
          label: String(article),
          value: article,
        })),
    [rows],
  );
  const filteredRows = selectedArticles.length
    ? rows.filter((row) => selectedArticles.includes(row.article))
    : rows;

  function updateMaxIndexPercentage(value) {
    setMaxIndexPercentageInput(value);

    const numberValue = Number(value);

    if (Number.isFinite(numberValue) && numberValue > 0) {
      setMaxIndexPercentage(numberValue);
      saveMaxIndexPercentage(numberValue);
    }
  }

  const showError = useCallback((detail) => {
    if (!toast.current) {
      return;
    }

    toast.current.show({
      severity: "error",
      summary: "Erro",
      detail,
      life: 5000,
    });
  }, []);

  const showSuccess = useCallback((detail) => {
    if (!toast.current) {
      return;
    }

    toast.current.show({
      severity: "success",
      summary: "Sucesso",
      detail,
      life: 3000,
    });
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);

    try {
      const response = await api.get("indice-venda-corte");
      const responseRows = Array.isArray(response.data)
        ? response.data
        : response.data.rows || response.data.data || [];
      const tableData = buildTableData(responseRows);

      setRows(tableData.rows);
      setMonthColumns(tableData.months);
      showSuccess("Dados carregados com sucesso.");
    } catch (error) {
      const message =
        error.response && error.response.data && error.response.data.message
          ? error.response.data.message
          : "Não foi possível carregar os dados.";

      showError(message);
    } finally {
      setLoading(false);
    }
  }, [showError, showSuccess]);

  function renderHeader() {
    return (
      <ColumnGroup>
        <Row>
          {fixedColumns.map((column) => (
            <Column
              key={column.field}
              header={column.header}
              rowSpan={2}
              headerStyle={compactHeaderStyle}
            />
          ))}
          {monthColumns.map((period) => (
            <Column
              key={period}
              header={period}
              colSpan={metricColumns.length}
              headerStyle={compactHeaderStyle}
            />
          ))}
        </Row>
        <Row>
          {monthColumns.map((period) => {
            const periodKey = createPeriodKey(period);

            return metricColumns.map((column) => (
              <Column
                key={`${periodKey}${column.suffix}Header`}
                header={column.header}
                headerStyle={metricStyles[column.key].headerStyle}
              />
            ));
          })}
        </Row>
      </ColumnGroup>
    );
  }

  function renderCardFooter() {
    return (
      <div className="p-d-flex p-jc-end">
        <Button
          label="Atualizar"
          icon="pi pi-refresh"
          onClick={fetchData}
          disabled={loading}
        />
      </div>
    );
  }

  function renderTableToolbar() {
    return (
      <div className="p-d-flex p-jc-between p-ai-center p-mb-2">
        {renderMetricLegend()}
        <div className="p-d-flex p-ai-center">
          <label
            htmlFor="max-index-percentage"
            className="p-mr-2"
            style={{ fontSize: 12, whiteSpace: "nowrap" }}
          >
            Limite V/C
          </label>
          <input
            id="max-index-percentage"
            type="number"
            min="1"
            value={maxIndexPercentageInput}
            onChange={(event) => updateMaxIndexPercentage(event.target.value)}
            onBlur={() => setMaxIndexPercentageInput(String(maxIndexPercentage))}
            style={{
              fontSize: 12,
              height: 28,
              marginRight: 8,
              width: 72,
            }}
          />
          <MultiSelect
            value={selectedArticles}
            options={articleOptions}
            onChange={(event) => setSelectedArticles(event.value)}
            placeholder="Filtrar artigos"
            filter
            display="chip"
            maxSelectedLabels={2}
            style={{
              fontSize: 12,
              minHeight: 28,
              width: 240,
            }}
          />
        </div>
      </div>
    );
  }

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return (
    <div>
      <Toast ref={toast} />
      <Menu />

      <Card title="Índice V/C" footer={renderCardFooter()}>
        {loading && <Loading />}
        {renderTableToolbar()}

        <DataTable
          className="p-datatable-sm"
          value={filteredRows}
          headerColumnGroup={renderHeader()}
          emptyMessage="Nenhum registro encontrado."
          responsiveLayout="scroll"
        >
          {fixedColumns.map((column) => (
            <Column
              key={column.field}
              field={column.field}
              bodyStyle={column.bodyStyle}
              body={
                column.format
                  ? (row) => column.format(row[column.field])
                  : undefined
              }
              sortable={column.sortable}
            />
          ))}
          {monthColumns.map((period) => {
            const periodKey = createPeriodKey(period);

            return metricColumns.map((column) => (
              <Column
                key={`${periodKey}${column.suffix}`}
                field={`${periodKey}${column.suffix}`}
                bodyStyle={metricStyles[column.key].bodyStyle}
                body={(row) =>
                  column.format(
                    row[`${periodKey}${column.suffix}`],
                    maxIndexPercentage,
                  )
                }
                sortable
              />
            ));
          })}
        </DataTable>
      </Card>
    </div>
  );
}
